import { z } from "zod";
import { SellBalanceInputSchema, SellBalanceSchema } from "./sell-balance.js";

export const WITHDRAWAL_BALANCES_MAX_ACCOUNTS = 16;
export const WITHDRAWAL_BALANCES_MAX_ASSETS = 256;
export const WITHDRAWAL_BALANCES_TTL_MS = 30_000;

const sortedUnique = (values: readonly string[]) =>
  values.every((value, index) => index === 0 || values[index - 1]! < value);
const accountRef = z
  .string()
  .regex(/^eip155:[1-9]\d*:0x[0-9a-f]{40}$/u)
  .refine((value) => Number.isSafeInteger(Number(value.split(":")[1])));
const accountRefs = z
  .array(accountRef)
  .min(1)
  .max(WITHDRAWAL_BALANCES_MAX_ACCOUNTS)
  .refine(sortedUnique, {
    message: "Accounts must be canonical, sorted, and unique",
  });
const scope = { portfolioId: z.string().trim().min(1).max(256), accountRefs };

/**
 * The caller selects the exact portfolio EVM account set, never amounts.
 *
 * Without `assetIds` the engine discovers candidates from indexed inventory
 * (an expensive aggregate). With `assetIds` — the candidate set of an earlier
 * full read of the same scope — it skips discovery and re-reads exactly those
 * assets live. Each asset must be on one of the requested accounts' chains.
 */
export const WithdrawalBalancesInputSchema = z
  .object({
    ...scope,
    assetIds: z
      .array(z.string())
      .min(1)
      .max(WITHDRAWAL_BALANCES_MAX_ASSETS)
      .refine(sortedUnique, {
        message: "Assets must be sorted and unique",
      })
      .optional(),
  })
  .strict()
  .refine(
    (value) =>
      (value.assetIds ?? []).every((assetId) =>
        value.accountRefs.some(
          (accountRef) =>
            SellBalanceInputSchema.safeParse({
              portfolioId: value.portfolioId,
              accountRef,
              assetId,
            }).success,
        ),
      ),
    {
      message:
        "Each asset must be canonical and on a requested account's chain",
    },
  );
export type WithdrawalBalancesInput = z.infer<
  typeof WithdrawalBalancesInputSchema
>;

const rowIdentity = {
  accountRef,
  chainRef: z.string().regex(/^eip155:[1-9]\d*$/u),
  assetId: z.string(),
};
const rawAmount = z.string().regex(/^(?:0|[1-9]\d*)$/u);
const identityValid = (row: {
  accountRef: string;
  assetId: string;
  chainRef: string;
}) =>
  row.accountRef.startsWith(`${row.chainRef}:`) &&
  SellBalanceInputSchema.safeParse({
    portfolioId: "validation",
    accountRef: row.accountRef,
    assetId: row.assetId,
  }).success;
const balance = z
  .object({
    ...rowIdentity,
    decimals: z.number().int().min(0).max(255),
    symbol: z.string().trim().min(1),
    priceUsd: z.number().positive().optional(),
    onchainRaw: rawAmount,
    heldRaw: rawAmount,
    spendableRaw: rawAmount,
    blockNumber: rawAmount,
  })
  .strict()
  .refine(identityValid, {
    message: "Balance identities must share a canonical EVM chain",
  });
const unavailableAsset = z
  .object({
    ...rowIdentity,
    blockNumber: rawAmount.optional(),
    reason: z.enum([
      "metadata_unavailable",
      "balance_unavailable",
      "source_debit_block_unproven",
      "pinned_block_before_source_debit",
    ]),
  })
  .strict()
  .refine(identityValid, {
    message: "Unavailable asset identities must share a canonical EVM chain",
  });

/** Advisory live balances. Indexed discovery does not certify exhaustive chain inventory. */
export const WithdrawalBalancesSchema = z
  .object({
    ...scope,
    capturedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    /** `skipped`: a refresh of caller-supplied `assetIds`; discovery did not run. */
    inventoryStatus: z.enum(["indexed", "unavailable", "skipped"]),
    indexedAsOf: z.iso.datetime().nullable(),
    /**
     * Discovered candidates beyond the 256-row read bound (typically spam
     * airdrops). Their balances were not read; nothing about them is implied.
     */
    omittedAssetCount: z.number().int().nonnegative(),
    balances: z.array(balance).max(WITHDRAWAL_BALANCES_MAX_ASSETS),
    unavailableAssets: z
      .array(unavailableAsset)
      .max(WITHDRAWAL_BALANCES_MAX_ASSETS),
  })
  .strict()
  .refine(
    (value) =>
      Date.parse(value.expiresAt) ===
      Date.parse(value.capturedAt) + WITHDRAWAL_BALANCES_TTL_MS,
    { message: "Withdrawal balance observations expire after 30 seconds" },
  )
  .refine(
    (value) =>
      (value.inventoryStatus === "indexed") === (value.indexedAsOf !== null),
    { message: "Indexed inventory requires an accounting timestamp" },
  )
  .refine(
    (value) =>
      value.inventoryStatus !== "skipped" || value.omittedAssetCount === 0,
    { message: "A refresh reads every requested asset" },
  )
  .refine(
    (value) => {
      const rows = [...value.balances, ...value.unavailableAssets];
      return (
        rows.length <= WITHDRAWAL_BALANCES_MAX_ASSETS &&
        rows.every((row) => value.accountRefs.includes(row.accountRef)) &&
        new Set(rows.map((row) => `${row.accountRef}/${row.assetId}`)).size ===
          rows.length
      );
    },
    {
      message:
        "Each candidate must belong to one requested account and occur once",
    },
  )
  .refine(
    (value) =>
      value.balances.every(
        (row) =>
          SellBalanceSchema.safeParse({
            portfolioId: value.portfolioId,
            capturedAt: value.capturedAt,
            expiresAt: value.expiresAt,
            status: "ready",
            accountRef: row.accountRef,
            assetId: row.assetId,
            chainRef: row.chainRef,
            blockNumber: row.blockNumber,
            onchainRaw: row.onchainRaw,
            heldRaw: row.heldRaw,
            spendableRaw: row.spendableRaw,
          }).success,
      ),
    { message: "Spendable balances must equal the raw balance after holds" },
  );

export type WithdrawalBalances = z.infer<typeof WithdrawalBalancesSchema>;
